// lib/tolerantJson.ts
/**
 * Shared primitives for reading JSON out of model output.
 *
 * WHY here rather than in a caller: three separate places needed "find the balanced JSON
 * value in this text" and each had grown its own brace counter. Two of those counters were
 * not string-aware, so a `{` or `}` inside a quoted value threw the depth off and the whole
 * payload was discarded (see `DirectorStreamParser`, which then printed the script to the
 * user as chat text). One correct implementation, used everywhere.
 */

/** Drop a leading ```json / ``` fence and the trailing one. */
export const stripCodeFences = (text: string): string => {
  let clean = text.trim();
  clean = clean.replace(/^```json\s*/i, '').replace(/^```\s*/i, '');
  clean = clean.replace(/\s*```$/, '');
  return clean.trim();
};

/**
 * First balanced `{...}` or `[...]` in the text, string- and escape-aware so braces inside
 * quoted values do not close the scan early.
 */
const extractFirstJsonValue = (text: string): string | null => {
  const source = text.trim();
  const openers: Record<string, string> = { '{': '}', '[': ']' };
  let start = -1;
  let closer = '';
  let depth = 0;
  let inString = false;
  let escaping = false;

  for (let i = 0; i < source.length; i++) {
    const char = source[i];
    if (!char) continue;

    if (escaping) {
      escaping = false;
      continue;
    }
    if (char === '\\') {
      escaping = true;
      continue;
    }
    if (char === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;

    if (depth === 0 && openers[char]) {
      start = i;
      closer = openers[char];
      depth = 1;
      continue;
    }
    if (depth > 0) {
      if (char === source[start]) depth += 1;
      else if (char === closer) {
        depth -= 1;
        if (depth === 0 && start >= 0) return source.slice(start, i + 1);
      }
    }
  }

  return null;
};

/**
 * Escape the raw control characters models leave inside string literals. A hard-wrapped
 * `rationale` or `reason` field arrives with a real newline in it, which `JSON.parse`
 * rejects outright even though the value is otherwise fine.
 */
const escapeControlCharsInStrings = (text: string): string => {
  const ESCAPES: Record<string, string> = { '\n': '\\n', '\r': '\\r', '\t': '\\t', '\b': '\\b', '\f': '\\f' };
  let out = '';
  let inString = false;
  let escaping = false;

  for (const char of text) {
    if (escaping) {
      out += char;
      escaping = false;
      continue;
    }
    if (char === '\\') {
      out += char;
      escaping = true;
      continue;
    }
    if (char === '"') {
      inString = !inString;
      out += char;
      continue;
    }
    if (inString && char < ' ') {
      out += ESCAPES[char] ?? `\\u${char.charCodeAt(0).toString(16).padStart(4, '0')}`;
      continue;
    }
    out += char;
  }

  return out;
};

/**
 * Parse text that should contain a JSON value, trying progressively more tolerant readings:
 * as-is, then the first balanced value, then the same with control characters escaped.
 * Returns `null` rather than throwing, callers here always have a degraded path.
 */
export const parseTolerantJson = <T = unknown>(text: string): T | null => {
  const candidates = [text, extractFirstJsonValue(text)].filter((c): c is string => !!c);
  for (const candidate of candidates) {
    for (const attempt of [candidate, escapeControlCharsInStrings(candidate)]) {
      try {
        return JSON.parse(attempt) as T;
      } catch {
        // fall through to the next, more tolerant reading
      }
    }
  }
  return null;
};
